import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Table from "components/DesignSystem/Table/V2/Table";
import { FileIcon } from "components/design/FileIcon";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
} from "react-table-8.10.7";
import { formatDate } from "utils/formatDate";
import { getFileType } from "utils/getFileType";
import { RecycleBinActions } from "./RecycleBinActions";
import { RecycleBinBulkActions } from "./RecycleBinBulkActions";
import { fileAndFolder } from "types/Models/documents";
import { useMemo, useState } from "react";

type RecycleBinListViewProps = {
  items: fileAndFolder[];
  groupId: string;
};

export const RecycleBinListView = ({
  items,
  groupId,
}: RecycleBinListViewProps) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const selectedItems = useMemo(() => {
    return items.filter((item) => rowSelection[item.uuid]);
  }, [items, rowSelection]);

  const selectedItemLength = selectedItems.length;

  const clearSelection = () => setRowSelection({});

  const createColumn = createColumnHelper<fileAndFolder>();

  const columns = [
    createColumn.display({
      id: "select",
      size: 5,
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    }),
    createColumn.accessor("name", {
      id: "name",
      size: 35,
      header: "Name",
      cell: (info) => {
        const { name, file_type, type, is_private } = info.row.original;
        const fileType = getFileType(file_type, type, false, is_private);

        return (
          <span className="t-flex t-items-center t-gap-3 t-overflow-hidden">
            <span className="t-shrink-0">
              <FileIcon fileType={fileType} size="32" />
            </span>
            <span className="t-truncate t-text-body-sm t-text-text-60">
              {name}
            </span>
          </span>
        );
      },
    }),
    createColumn.accessor("owner", {
      size: 20,
      header: "Owner",
      cell: (info) => (
        <span className="t-text-body-sm t-text-text-60">
          {info.getValue() || "-"}
        </span>
      ),
    }),
    createColumn.accessor("action_at", {
      size: 15,
      header: "Deleted on",
      cell: (info) => {
        const { created_at } = info.row.original;
        return (
          <span className="t-text-body-sm t-text-text-60">
            {created_at ? formatDate(created_at) : "-"}
          </span>
        );
      },
    }),
    createColumn.accessor("action_by", {
      size: 20,
      header: "Deleted by",
      cell: (info) => (
        <span className="t-text-body-sm t-text-text-60">
          {info.getValue() || "-"}
        </span>
      ),
    }),
    createColumn.display({
      id: "action",
      size: 5,
      header: "",
      cell: (info) => {
        const { uuid, name } = info.row.original;
        return (
          <RecycleBinActions
            fileId={uuid}
            groupId={groupId}
            selectedItemLength={selectedItemLength}
          />
        );
      },
    }),
  ];

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.uuid,
    enableRowSelection: true,
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  return (
    <div className="t-flex t-flex-col t-gap-4">
      {selectedItems.length > 0 && (
        <RecycleBinBulkActions
          selectedItems={selectedItems}
          groupId={groupId}
          onClearSelection={clearSelection}
        />
      )}
      <Table.Container layout="fixed">
        <Table.Content>
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.HeadCell
                    key={header.id}
                    style={{ width: `${header.getSize()}%` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.HeadCell>
                ))}
              </Table.Row>
            ))}
          </Table.Head>
          <Table.Body>
            {table.getRowModel().rows.map((row) => {
              const isSelected = row.getIsSelected();
              return (
                <Table.Row
                  key={row.id}
                  className={isSelected ? "t-bg-surface-purple" : ""}
                  onClick={() => row.toggleSelected(true)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      style={{ width: `${cell.column.getSize()}%` }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Content>
      </Table.Container>
    </div>
  );
};
